import config from "@/config";
import NotificationSound from '../assets/notification.wav';
import {Conversation} from "@/models";
import {Feature} from "@/util";
import moment from "moment";

export default (app) => {
    return {
        namespaced: true,

        state: () => ({
            /* General */
            loaded: false,
            connected: false,
            reconnecting: false,
            activeUsers: [],
            contacts: [],
            conversations: {},
            conversationTypingObject: {},
            currentChatUser: null,
            peerState: 'ready',
            onGoingCall: false,

            /* Operator specific */
            internalUsers: [],
        }),

        getters: {
            /* General */
            onGoingCall: (state) => state.onGoingCall,
            peerState: (state) => state.peerState,
            currentChatUser: (state) => state.currentChatUser,
            conversations: (state) => state.currentChatUser ? state.conversations[state.currentChatUser.id] : [],
            conversationsForUserId: (state) => (userId) => state.conversations[userId] || [],
            connected: (state) => state.connected,
            activeUsers: (state) => state.activeUsers,
            contacts: (state) => state.contacts,

            /* Conversation specific */
            newMessagesCount: (state) => {
                let count = 0;

                for (let userId in state.conversations) {
                    const conversations = state.conversations[userId];

                    count += conversations.reduce((a, e) => {
                        return a + ((e.messages || []).filter(m => userId !== m.user.id && !m.seen)).length;
                    }, 0);
                }

                return count;
            },
            newMessagesCountForUser: (state) => (userId) => {
                return (state.conversations[userId] || []).findIndex(e => {
                    return (e.messages || []).find(m => userId !== m.user.id && !m.seen);
                }) >= 0;
            },
            conversationTyping: (state) => (conversationId) => {
                return !!state.conversationTypingObject[conversationId];
            },

            unseenMessages: () => (param = null) => 0, // @todo

            // Return number of unseen messages of a conversation for a specific user
            // Get messages from the other user and check which ones are unseen
            unseenMessagesForUser: (state) => (conversation, whichUser = 'current') => {
                let msgFromUserId;

                if (whichUser === 'current') {
                    msgFromUserId = conversation.user.id; // other user
                } else {
                    msgFromUserId = state.currentChatUser.id; // current user
                }

                return conversation.messages.filter(e => e.user_id === msgFromUserId && !e.seen).length;
            },

            unseenMessagesForOperatorUser: (state) => (userId) => {
                let messages = 0;

                state.conversations[userId].forEach(e => {
                    messages += (e.messages || []).filter(m => userId !== m.user.id && !m.seen).length;
                });

                return messages;
            },

            lastMessage: (state) => (conversation) => {
                const messages = (conversation.messages || []).filter(e => e.type !== 'call');
                return messages.length ? messages[messages.length - 1] : null;
            },

            /* Subscription specific and message limits */
            messageLimitExceeded: state => {
                if (state.internalUsers.length || !state.currentChatUser) {
                    return false;
                }

                const user = state.currentChatUser;
                if (!user.subscription || user.subscription.plan.name !== 'trial') {
                    return false;
                }

                const conversations = state.conversations[state.currentChatUser.id] || [];
                const currentUserMessagesCount = conversations.reduce((a, e) => {
                    return a + ((e.messages || []).filter(m => state.currentChatUser.id === m.user.id)).length;
                }, 0);

                if (currentUserMessagesCount < 15) {
                    return false;
                }

                return true;
            },

            remainingMessagesCount: state => {
                const conversations = state.conversations[state.currentChatUser.id] || [];
                return 15 - conversations.reduce((a, e) => {
                    return a + ((e.messages || []).filter(m => state.currentChatUser.id === m.user.id)).length;
                }, 0);
            },

            /* Operator specific */
            internalUsers: (state) => state.internalUsers,
        },

        mutations: {
            setCurrentChatUser(state, data) {
                state.currentChatUser = data;
            },

            setConnected(state, data) {
                state.connected = data;
            },

            setReconnecting(state, data) {
                state.reconnecting = data;
            },

            setActiveUsers(state, data) {
                let usersObject = {};
                state.activeUsers.forEach(e => {
                    usersObject[e.id] = e.id;
                });

                data.forEach(user => {
                    if (typeof user === 'object') {
                        if (!state.activeUsers.includes(user?.id)) {
                            state.activeUsers.push(user?.id);
                        }
                    } else {
                        if (!state.activeUsers.includes(user)) {
                            state.activeUsers.push(user);
                        }
                    }
                })
            },

            setContacts(state, data) {
                state.contacts = data;
            },

            async updateConversation(state, data) {
                const index = state.conversations[state.currentChatUser.id].findIndex(e => e.id === data.conversation_id);

                Object.keys(data).forEach(key => {
                    state.conversations[state.currentChatUser.id][index][key] = data[key];
                });
            },

            async markConversationAsRead(state, {conversation, messagesForUserId, isListener}) {
                if (!state.currentChatUser || !conversation) {
                    return;
                }

                const index = state.conversations[state.currentChatUser.id]?.findIndex(e => e.id === conversation.id);

                if (index < 0) {
                    return;
                }

                state.conversations[state.currentChatUser.id][index].messages.forEach((message, msgIndex) => {
                    if (message.user_id === messagesForUserId) {
                        state.conversations[state.currentChatUser.id][index].messages[msgIndex].seen = true;
                    }
                });

                if (!isListener && conversation.user?.id && conversation.messages?.length) {
                    window.AppEcho.private(`chat_messages.${conversation.user.id}`)
                        .then(c => {
                           c.whisper('seen', {
                               conversation_id: conversation.id,
                           });
                        });
                }
            },

            async updateContactsList(state, newContact) {
                const index = state.contacts.findIndex(e => e.id === newContact.id);
                if (index >= 0) {
                    state.contacts.splice(index, 1, newContact);
                } else {
                    state.contacts.push(newContact);
                }
            },

            async updateOperatorUser(state, userData) {
                const index = state.internalUsers.findIndex(e => e.id === userData.id);
                if (index >= 0) {
                    state.internalUsers.splice(index, 1, userData);
                }
            },
        },

        actions: {
            async init({rootState, state, commit, dispatch}) {
                await dispatch('getContacts');

                let localPeers = [];

                if (rootState.isOperator) {
                    state.internalUsers = await dispatch('operator/getOperatorUsers', {}, {root: true});

                    state.internalUsers.forEach(e => {
                        state.conversations[e.id] = state.conversations[e.id] ?? [];
                        state.conversations[e.id] = e.conversations.map(c => Conversation(c, rootState));
                        delete e.conversations;

                        localPeers.push(e.id);
                    });
                } else if (rootState.isCustomer) {
                    await commit('setCurrentChatUser', rootState.auth.authUser);
                    localPeers.push(rootState.auth.authUser.id);

                    await dispatch('getCustomerConversations');
                }

                // Init Peers
                if (rootState.isOperator) {
                    window.Peer.register(localPeers);
                } else {
                    window.Peer.register([rootState.auth.authUser.id]);
                }

                localPeers.forEach(id => {
                    window.Peer
                        .on('connection', (conn, peer) => {
                            console.log('conn', conn);
                            app.config.globalProperties.$eventHub.$emit('PeerConnection', {
                                localPeerId: id,
                                peer,
                            });
                        })
                        .on('open', peer => {
                            console.log('conn.open');
                            app.config.globalProperties.$eventHub.$emit('PeerConnectionOpen', {
                                localPeerId: id,
                                peer,
                            });
                        })
                        .on('data', (message, peer) => {
                            if (state.peerState !== 'connected') {
                                return;
                            }

                            console.log('conn.data', message);
                            app.config.globalProperties.$eventHub.$emit('PeerData', {
                                localPeerId: id,
                                message,
                                peer,
                            });
                        })
                        .on('handshake', (peer, userId) => {
                            console.log('conn.handshake');
                            console.log(peer, userId);
                            if (state.peerState !== 'handshake') {
                                return;
                            }

                            app.config.globalProperties.$eventHub.$emit('PeerHandshake', {
                                localPeerId: id,
                                peer,
                                userId,
                            });
                        })
                        .on('confirm_handshake', (peer, userId) => {
                            console.log('conn.confirm_handshake', peer, userId);
                            if (state.peerState !== 'connected') {
                                return;
                            }

                            app.config.globalProperties.$eventHub.$emit('PeerConfirmHandshake', {
                                localPeerId: id,
                                peer,
                                userId,
                            });
                        })
                        .on('refuse_handshake', (peer, userId) => {
                            console.log('conn.refuse_handshake', peer, userId);
                            app.config.globalProperties.$eventHub.$emit('PeerRefuseHandshake', {
                                localPeerId: id,
                                peer,
                                userId,
                            });
                        })
                        .on('close', (peer, userId) => {
                            console.log('conn.close', peer, userId);
                            app.config.globalProperties.$eventHub.$emit('PeerConnectionClosed', {
                                localPeerId: id,
                                peer,
                                userId,
                            });
                        })
                        .on('state', (value) => {
                            console.log('conn.state', value);

                            if (value === 'connected') {
                                state.onGoingCall = true;
                            } else {
                                state.onGoingCall = false;
                            }

                            state.peerState = value;
                        });
                })

                const users = [
                    ...rootState.chat.contacts,
                    ...rootState.people,
                ];
                let activeUsers = [];
                users.forEach(e => {
                    if (e.is_online) {
                        activeUsers.push(e.id);
                    }
                });

                commit('setActiveUsers', activeUsers);

                state.loaded = true;
            },

            async getContacts({commit, rootState}, filters = {}) {
                const r = await window.http.get(`${config.API_URL}/contacts`);

                commit('setContacts', r.data);

                return r;
            },

            async bulkMessage({commit, rootState}, data) {
                return window.http.post(`${config.API_URL}/chat/bulk-message`, data);
            },

            async startConversation({state, dispatch, rootState}, {username}) {
                if (!state.currentChatUser) {
                    return;
                }

                let conversation = state.conversations[state.currentChatUser.id]?.find(e => e.user.username === username);
                if (conversation) {
                    return conversation;
                }

                let user = state.contacts.find(e => e.username === username);

                if (!user) {
                    await dispatch('fetchContact', username);
                    user = rootState.contacts.find(e => e.username === username);
                }

                state.conversations[state.currentChatUser.id].push(
                    Conversation({first_user_id: state.currentChatUser.id, second_user_id: user.id}, rootState)
                );

                return conversation;
            },

            async fetchContact({commit}, id) {
                const r = await window.http.get(`${config.API_URL}/users/${id}`);

                if (r.success) {
                    await commit('updateContactsList', r.data);
                }

                return r.data;
            },

            async addMessageToConversation({rootState, state, dispatch}, message) {
                const conversationKey = message.to_user_id ? message.to_user_id : state.currentChatUser.id;
                let index = state.conversations[conversationKey].findIndex(e => e.id === message.conversation_id);

                if (index < 0) {
                    if (rootState.isCustomer) {
                        await dispatch('getCustomerConversations');
                        index = state.conversations[conversationKey].findIndex(e => e.id === message.conversation_id);
                    } else {
                        const r = await dispatch('getConversation', message.conversation_id);
                        const conversation = Conversation(r.data, rootState);

                        // Replace `new` or add conversation
                        index = state.conversations[conversationKey].findIndex(i => i.user.id === conversation.user.id);

                        if (index >= 0) {
                            state.conversations[conversationKey].splice(index, 1, conversation);
                        } else {
                            state.conversations[conversationKey].push(conversation);
                        }

                        index = state.conversations[conversationKey].findIndex(e => e.id === message.conversation_id);
                    }
                } else {
                    const msg = state.conversations[conversationKey][index].createMessage(message);
                    state.conversations[conversationKey][index].messages.push(msg);
                }

                state.conversations[conversationKey][index].reload();
            },

            async markConversationAsRead({commit, dispatch, state}, {conversation, messagesForUserId}) {
                if (conversation.id === 'new' && state.currentChatUser) {
                    await dispatch('getCustomerConversations');
                }

                commit('markConversationAsRead', {conversation, messagesForUserId});

                await window.http.post(`${config.API_URL}/conversations/${conversation.id}/read`);
            },

            connect({state, commit, dispatch, rootState}) {
                if (!state.connected || state.reconnecting) {
                    const feature = app.config.globalProperties.$store.getters['features/feature'];
                    const authUser = app.config.globalProperties.$store.getters['auth/user'];
                    const $eventHub = app.config.globalProperties.$eventHub;

                    let users = [authUser.id];
                    if (rootState.isOperator) {
                        users = state.internalUsers.map(e => e.id);
                    }

                    for (let userId of users) {
                        // Chat Events
                        console.log('Listen', `chat_messages.${userId}`)
                        window.AppEcho.private(`chat_messages.${userId}`)
                            .then(c => {
                                c.listen('.ChatMessage', async (e) => {
                                    console.log('.ChatMessage', e);
                                    if (e.message.type === 'call') {
                                        $eventHub.$emit('VideoChatCall', {
                                            conversation_id: e.message.conversation_id,
                                            content: e.message.content,
                                            user_id: e.message.user_id,
                                            to_user_id: e.message.to_user_id,
                                        });
                                    } else {
                                        await dispatch('addMessageToConversation', e.message);
                                        $eventHub.$emit('ChatMessage', e.message);

                                        if (feature('notifications_sound') === Feature.ENABLED) {
                                            try {
                                                (new Audio(NotificationSound)).play()
                                                    .then()
                                                    .catch();
                                            } catch (e) {
                                                //
                                            }
                                        }
                                    }
                                })
                                c.listenForWhisper('typing', e => {
                                        state.conversationTypingObject = {...state.conversationTypingObject, [e.conversation_id]: e.typing};
                                    })
                                c.listenForWhisper('seen', async (e) => {
                                        if (e.conversation_id) {
                                            let conversation = state.conversations[userId].find(e => e.id === e.conversation_id);
                                            if (!conversation) {
                                                const r = await dispatch('getConversation', e.conversation_id);
                                                conversation = Conversation(r.data, rootState);

                                                // Replace `new` or add conversation
                                                const index = state.conversations[userId].findIndex(i => i.user.id === conversation.user.id);

                                                if (index >= 0) {
                                                    state.conversations[userId].splice(index, 1, conversation);
                                                } else {
                                                    state.conversations[userId].push(conversation);
                                                }
                                            }

                                            if (conversation) {
                                                commit('markConversationAsRead', {conversation, messagesForUserId: userId, isListener: true});
                                            }
                                        }
                                    });
                            });
                    }

                    window.AppEcho.join('presence', (list) => { commit('setActiveUsers', list); console.log('active', list) });

                    commit('setConnected', true);
                }
            },

            async sendMessage({state, commit}, {conversationId, type, content, to_user_id, user_id}) {
                const r = await window.http.post(`${config.API_URL}/conversations/${conversationId}/message`, {
                    user_id: state.currentChatUser ? state.currentChatUser.id : user_id, type, content, to_user_id
                });

                if (r.data.conversation_id) {
                    const index = state.conversations[state.currentChatUser.id].findIndex(e => (e.id === 'new' && e.user.id === to_user_id) || e.id === r.data.conversation_id);
                    state.conversations[state.currentChatUser.id][index].id = r.data.conversation_id;
                }

                return r;
            },

            async getCustomerConversations({state, rootState}, filters = {}) {
                const r = await window.http.get(`${config.API_URL}/conversations`);
                state.conversations = {
                    [state.currentChatUser.id]: r.data.map(e => Conversation(e, rootState)),
                };

                return r;
            },

            async getConversation({}, id) {
                return await window.http.get(`${config.API_URL}/conversations/${id}`);
            },

            async getProcessedConversation({rootState}, id) {
                const r = await window.http.get(`${config.API_URL}/conversations/${id}`);
                return Conversation(r.data, rootState);
            },
        }
    };
};
